import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ChevronLeft, PartyPopper, ChevronRight } from 'lucide-react';
import {
  SHIFT_SHORT,
  TIMEZONE,
  addDays,
  fmtDateLong,
  fmtTime,
  fmtWeekday,
  localYmd,
  workDateFor,
  type Shift,
} from '@siva/shared';
import { DateField } from '../../lib/DateField.js';
import { api, post } from '../../lib/api.js';
import { errorMessage } from '../../lib/passkey.js';
import { Alert, fmtDate } from '../../lib/ui.js';
import {
  Avatar,
  BarChart,
  Card,
  EmptyState,
  Skeleton,
  StackedMini,
  Tabs,
  type Tone,
} from '../../lib/components.js';
import { t } from '../../i18n/index.js';

type Base = {
  id: string;
  name: string;
  employment_type: 'full_time' | 'part_time' | null;
  shift: Shift;
  target_minutes: number;
  off_day: boolean;
  off_reason: 'weekly_off' | 'holiday' | null;
};
type Day = {
  work_date: string;
  is_today: boolean;
  previous_date: string | null;
  next_date: string | null;
  earliest_date: string;
  server_time: string;
  /** The day cutoff in force, "HH:MM": with server_time, which working day it is now. */
  cutoff: string;
  source: 'live' | 'summaries';
  holiday: string | null;
  counts: {
    punched_in: number;
    punched_out: number;
    not_punched: number;
    weekly_off: number;
    holiday: number;
    off: number;
    no_punchout: number;
    /** Workers employed on this date; the groups above sum to it. */
    active: number;
  };
  punched_in: (Base & {
    in_time: string;
    running_minutes: number;
    weak_gps: boolean;
    location_unverified: boolean;
  })[];
  punched_out: (Base & {
    first_in: string;
    last_out: string;
    worked_minutes: number;
    below_target: boolean;
    auto_closed: boolean;
    weak_gps: boolean;
    location_unverified: boolean;
  })[];
  not_punched: Base[];
  off: Base[];
  flags_open: number;
};
type EarlyOut = {
  id: string;
  worker: { id: string; name: string };
  work_date: string;
  worked_minutes: number;
  target_minutes: number;
  reason_code: string;
  reason: string;
};
import { type Flag } from '../flag-kinds.js';
import { usePhotos } from '../photos.js';
import { DayBoard, type ComingUp } from '../DayBoard.js';
type WeekSummary = {
  week_start: string;
  today: string;
  days: {
    date: string;
    /** A day still to come this week: no bar, rather than a bar saying nobody came. */
    is_future: boolean;
    is_today: boolean;
    present: number;
    absent: number;
    off: number;
    holiday: number;
  }[];
};
type CellStatus = 'present' | 'present_no_punchout' | 'absent' | 'weekly_off' | 'holiday';
type WeekGrid = {
  week_start: string;
  today: string;
  days: { date: string; is_today: boolean; is_future: boolean }[];
  workers: {
    id: string;
    name: string;
    employment_type: 'full_time' | 'part_time' | null;
    shift: Shift;
    join_date: string | null;
    days: Record<
      string,
      {
        status: CellStatus;
        worked_minutes: number;
        target_minutes: number;
        auto_closed: boolean;
      } | null
    >;
  }[];
};

const hm = (min: number) => `${Math.floor(min / 60)}h ${String(min % 60).padStart(2, '0')}m`;
const hShort = (min: number) => (min >= 60 ? `${(min / 60).toFixed(1)}h` : `${min}m`);
const time = (iso: string) => (iso ? fmtTime(iso) : '—');
const todayYmd = () => localYmd(new Date());
const longDate = (ymd: string) => fmtDateLong(ymd);

export function AttendancePage() {
  const navigate = useNavigate();
  const { photoOf } = usePhotos();
  // The selected day and the view live in the URL: bookmarkable, and a refresh keeps them.
  const [params, setParams] = useSearchParams();
  /**
   * The working day, as the server counts it — the calendar date only until the server answers.
   *
   * They differ between midnight and the day cutoff (2:00 AM since 14 October 2026): the calendar
   * has moved on and the working day has not, and the page headed the old day's figures with the
   * new day's date. The first unparameterised load says which day it really is.
   */
  const [today, setToday] = useState(todayYmd);
  const dateParam = params.get('date');
  const date =
    dateParam && /^\d{4}-\d{2}-\d{2}$/.test(dateParam) && dateParam <= today ? dateParam : today;
  const view = params.get('view') === 'week' ? 'week' : 'day';
  const setUrl = (next: { date?: string; view?: 'day' | 'week' }) => {
    const d = next.date ?? date;
    const v = next.view ?? view;
    const p: Record<string, string> = {};
    if (d !== today) p.date = d;
    if (v === 'week') p.view = 'week';
    setParams(p, { replace: true });
  };

  const [data, setData] = useState<Day | null>(null);
  const [week, setWeek] = useState<WeekSummary | null>(null);
  const [grid, setGrid] = useState<WeekGrid | null>(null);
  const [flags, setFlags] = useState<Flag[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [earlyOuts, setEarlyOuts] = useState<EarlyOut[]>([]);
  const [comingUp, setComingUp] = useState<ComingUp | null>(null);
  /** Choosing workers in the lists that already show them, rather than in a second list. */

  const load = useCallback(async () => {
    try {
      const q = date === today ? '' : `?date=${date}`;
      const [d, f, w] = await Promise.all([
        api<Day>(`/admin/today${q}`).then((day) => {
          // Whatever day was asked for, the answer carries the server's clock and the cutoff, so
          // the working day is worked out by the very rule the server uses — and a reload on
          // ?date=yesterday still knows that it is yesterday.
          const working = workDateFor(new Date(day.server_time), TIMEZONE, day.cutoff);
          if (working !== today) setToday(working);
          return day;
        }),
        api<{ flags: Flag[] }>('/admin/flags?status=open'),
        api<WeekSummary>('/admin/week-summary').catch(() => null),
      ]);
      // The side panel's shift times, tomorrow and the next holiday. A failure leaves the panel
      // out rather than the page: every worker still shows, as Not Punched until it loads.
      setComingUp(await api<ComingUp>('/admin/today/coming-up').catch(() => null));
      setData(d);
      setFlags(f.flags);
      // Early punch-outs waiting on a decision. They are not tied to the day being viewed: an
      // admin should see them wherever they are on the page.
      try {
        const e = await api<{ early_outs: EarlyOut[] }>('/admin/early-outs');
        setEarlyOuts(e.early_outs);
      } catch {
        setEarlyOuts([]);
      }
      if (w) setWeek(w);
      if (view === 'week') setGrid(await api<WeekGrid>(`/admin/attendance/week?date=${date}`));
      setError(null);
    } catch (err) {
      setError(errorMessage(err));
    }
  }, [date, today, view]);

  useEffect(() => {
    void load();
    const id = setInterval(() => void load(), 30_000);
    return () => clearInterval(id);
  }, [load]);

  /** Approve credits the day to its target; reject leaves the shortfall where it is. */
  const decideEarlyOut = async (id: string, approve: boolean) => {
    setBusy(true);
    setError(null);
    try {
      await post(`/admin/early-outs/${id}/${approve ? 'approve' : 'reject'}`, {});
      await load();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  /**
   * Mark one flag, or a whole group of them, as looked at. Six identical fake-GPS flags from one
   * phone on one afternoon are one conversation, not six clicks — and the note, which is what
   * anybody reading the audit log later actually wants, is the same note for all of them.
   */

  // Every worker employed on this date lands in exactly one tile.
  const isToday = date === today;
  const yesterday = addDays(today, -1);
  // "Today · Saturday, 12 September 2026"; a plain past day shows the long date once.
  const heading = isToday
    ? `${t('nav_today')} · ${longDate(date)}`
    : date === yesterday
      ? `${t('today_yesterday')} · ${longDate(date)}`
      : longDate(date);
  const earliest = data?.earliest_date ?? date;
  const canPrev = date > earliest;

  const dateControl = (
    <div className="date-control" role="group" aria-label={t('att_date_group')}>
      <button
        type="button"
        className="icon-btn small"
        aria-label={t('att_prev_day')}
        disabled={!canPrev}
        onClick={() => setUrl({ date: addDays(date, -1) })}
      >
        <ChevronLeft size={16} aria-hidden="true" />
      </button>
      <DateField
        label={t('att_date')}
        value={date}
        min={earliest}
        max={today}
        onChange={(v) => setUrl({ date: v })}
      />
      <button
        type="button"
        className="icon-btn small"
        aria-label={t('att_next_day')}
        disabled={isToday}
        onClick={() => setUrl({ date: addDays(date, 1) })}
      >
        <ChevronRight size={16} aria-hidden="true" />
      </button>
      <span className="date-sep" aria-hidden="true" />
      <Tabs
        value={isToday ? 'today' : date === yesterday ? 'yesterday' : 'other'}
        onChange={(v) =>
          setUrl({ date: v === 'today' ? today : v === 'yesterday' ? yesterday : date })
        }
        items={[
          { value: 'today', label: t('nav_today') },
          { value: 'yesterday', label: t('today_yesterday') },
        ]}
      />
    </div>
  );

  if (error && !data) return <Alert>{error}</Alert>;
  if (!data) {
    return (
      <div className="stack">
        <div className="stat-row">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="stat-tile">
              <Skeleton lines={2} className="grow" />
            </div>
          ))}
        </div>
        <Card>
          <Skeleton lines={5} />
        </Card>
      </div>
    );
  }

  // No per-row Adjust here since 18 September 2026: the pencil crowded every list and wrapped
  // under names, and the owner adjusts a day from the worker's page (Select Days) or the
  // Reports day table instead.

  // Hours per worker vs target for the selected day (everyone with a punch).
  // "Hemanth" and "Hemanth" told nobody which was which: the first name, with the initial of
  // the last, is the shortest label that still tells two people apart.
  const chartName = (name: string) => {
    const parts = name.trim().split(/\s+/);
    return parts.length > 1 ? `${parts[0]} ${parts[parts.length - 1]![0]}` : parts[0]!;
  };
  const bars = [
    ...data.punched_in.map((w) => ({
      key: w.id,
      label: chartName(w.name),
      value: w.running_minutes,
      target: w.target_minutes,
      tone: 'teal' as Tone,
    })),
    ...data.punched_out.map((w) => ({
      key: w.id,
      label: chartName(w.name),
      value: w.worked_minutes,
      target: w.target_minutes,
      tone: (w.below_target ? 'amber' : 'violet') as Tone,
    })),
  ].sort((a, b) => a.label.localeCompare(b.label));
  const belowTarget = data.punched_out.filter((w) => w.below_target).length;
  // The distinct daily targets in play: one for full-time, one for part-time.
  const targetLevels = [...new Set(bars.map((b) => b.target).filter((n): n is number => !!n))].sort(
    (x, y) => y - x,
  );

  const cellTone = (c: NonNullable<WeekGrid['workers'][number]['days'][string]> | null) =>
    !c
      ? 'blank'
      : c.status === 'present'
        ? c.target_minutes > 0 && c.worked_minutes < c.target_minutes
          ? 'below'
          : 'present'
        : c.status === 'present_no_punchout'
          ? 'nopunchout'
          : c.status === 'absent'
            ? 'absent'
            : c.status === 'holiday'
              ? 'holiday'
              : 'off';

  return (
    <div className="stack">
      <div className="att-head">
        <h2>{heading}</h2>
        <div className="toolbar">
          {dateControl}
          {/* Select Workers left this page on 17 September 2026 at the owner's request. Bulk
              adjustment lives on the worker's calendar (Select Days); one day for one worker is
              the pencil on each row here. */}
          <span className="grow" />
          <Tabs
            value={view}
            onChange={(v) => setUrl({ view: v })}
            items={[
              { value: 'day', label: t('att_view_day') },
              { value: 'week', label: t('att_view_week') },
            ]}
          />
        </div>
      </div>
      {error && <Alert>{error}</Alert>}
      {data.holiday && (
        <div className="holiday-note" role="status">
          <PartyPopper size={16} aria-hidden="true" />
          <span>{t('today_is_holiday', { name: data.holiday })}</span>
        </div>
      )}

      {view === 'week' ? (
        <Card
          title={t('att_week_title', { week: grid ? fmtDate(grid.week_start, false) : '' })}
          subtitle={t('att_week_sub')}
        >
          {!grid ? (
            <Skeleton lines={6} />
          ) : (
            <div className="table-wrap">
              <table className="week-grid" data-testid="week-grid">
                <thead>
                  <tr>
                    <th>{t('worker_name')}</th>
                    {grid.days.map((d) => (
                      <th
                        key={d.date}
                        className={`${d.date === date ? 'selected' : ''} ${d.is_future ? 'future' : ''}`}
                      >
                        {fmtWeekday(d.date)}
                        <br />
                        <span className="muted">{d.date.slice(8)}</span>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {grid.workers.map((w) => (
                    <tr key={w.id}>
                      <td>
                        <span className="person">
                          <Avatar name={w.name} size={26} photoUrl={photoOf(w.id)} />
                          <span>
                            <Link to={`/workers/${w.id}?date=${date}`}>{w.name}</Link>{' '}
                            <small className="muted">
                              {w.employment_type === 'full_time' ? 'FT' : 'PT'}
                            </small>
                            {new Set(grid.workers.map((x) => x.shift)).size > 1 && (
                              <small className="muted"> {SHIFT_SHORT[w.shift]}</small>
                            )}
                          </span>
                        </span>
                      </td>
                      {grid.days.map((d) => {
                        const c = w.days[d.date] ?? null;
                        const beforeJoin = !!w.join_date && d.date < w.join_date;
                        const isToday = d.date === grid.today;
                        const tone = d.is_future
                          ? 'future'
                          : beforeJoin
                            ? 'blank'
                            : isToday && (!c || c.status === 'absent')
                              ? 'inprogress'
                              : cellTone(c);
                        const emptyTitle = d.is_future
                          ? t('att_cell_future')
                          : isToday
                            ? t('att_cell_in_progress')
                            : beforeJoin
                              ? t('att_cell_before_join', {
                                  date: fmtDate(w.join_date ?? '', false),
                                })
                              : t('att_cell_no_record');
                        return (
                          <td
                            key={d.date}
                            className={`cell ${tone}${d.date === date ? ' selected' : ''}`}
                            data-status={c?.status ?? ''}
                            role={d.is_future ? undefined : 'button'}
                            tabIndex={d.is_future ? undefined : 0}
                            title={
                              c
                                ? `${w.name} · ${fmtDate(d.date, false)} · ${c.status.replace('_', ' ')}${c.worked_minutes ? ` · ${hm(c.worked_minutes)}` : ''}`
                                : `${w.name} · ${fmtDate(d.date, false)} · ${emptyTitle}`
                            }
                            onClick={() => {
                              if (!d.is_future && !beforeJoin)
                                navigate(`/workers/${w.id}?date=${d.date}`);
                            }}
                            onKeyDown={(e) => {
                              if (!d.is_future && (e.key === 'Enter' || e.key === ' ')) {
                                e.preventDefault();
                                navigate(`/workers/${w.id}?date=${d.date}`);
                              }
                            }}
                          >
                            {d.is_future || beforeJoin
                              ? '—'
                              : c && (c.status === 'present' || c.status === 'present_no_punchout')
                                ? hShort(c.worked_minutes)
                                : isToday
                                  ? t('att_cell_in_progress')
                                  : c?.status === 'absent'
                                    ? t('att_absent')
                                    : c
                                      ? t('att_cell_off')
                                      : '—'}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="legend-dots">
                <span>
                  <i className="cell-swatch present" />
                  {t('hours_st_present')}
                </span>
                <span>
                  <i className="cell-swatch below" />
                  {t('today_below_target')}
                </span>
                <span>
                  <i className="cell-swatch nopunchout" />
                  {t('att_no_punchout')}
                </span>
                <span>
                  <i className="cell-swatch inprogress" />
                  {t('att_cell_in_progress')}
                </span>
                <span>
                  <i className="cell-swatch absent" />
                  {t('hours_st_absent')}
                </span>
                <span>
                  <i className="cell-swatch off" />
                  {t('att_off')}
                </span>
                <span>
                  <i className="cell-swatch holiday" />
                  {t('hours_st_holiday')}
                </span>
              </div>
            </div>
          )}
        </Card>
      ) : (
        <>
          {/* One table with a side panel, layout A2 chosen by the owner on 15 October 2026. It
              replaces five count tiles, four cards of names and the Open Flags card: the tabs are
              the counts, and the side panel says what is waiting and what is coming. */}
          <DayBoard
            data={data}
            comingUp={comingUp}
            earlyOuts={earlyOuts.length}
            flagsOpen={flags ? flags.length : null}
            previousDate={data.previous_date}
          />

          {earlyOuts.length > 0 && (
            <Card
              id="early-outs"
              className="early-outs-card"
              title={t('early_outs_title')}
              subtitle={t('early_outs_sub')}
            >
              <ul className="list">
                {earlyOuts.map((e) => (
                  <li key={e.id} className="swap-row">
                    <div>
                      <strong>{e.worker.name}</strong>{' '}
                      <span className="muted">
                        {fmtDate(e.work_date, false)} · {hm(e.worked_minutes)} {t('early_outs_of')}{' '}
                        {hm(e.target_minutes)}
                      </span>
                      <div className="muted small-text">
                        {t(`early_out_${e.reason_code}` as 'early_out_unwell')} — “{e.reason}”
                      </div>
                    </div>
                    <span className="row">
                      <button
                        type="button"
                        className="small"
                        disabled={busy}
                        onClick={() => void decideEarlyOut(e.id, true)}
                      >
                        {t('early_outs_approve')}
                      </button>
                      <button
                        type="button"
                        className="small secondary"
                        disabled={busy}
                        onClick={() => void decideEarlyOut(e.id, false)}
                      >
                        {t('early_outs_reject')}
                      </button>
                    </span>
                  </li>
                ))}
              </ul>
            </Card>
          )}

          {/* The tiles are the whole workforce for this date, split five ways, counted once by
              the server from one list of workers. There is nothing here to reconcile: if the
              server ever finds the split wrong it records it and raises it on the Admin page,
              rather than asking the reader to add the tiles up by hand. */}

          <div className="grid12 charts">
            <Card
              className="span-8"
              title={isToday ? t('today_hours_chart') : t('att_hours_chart')}
              subtitle={t('today_hours_chart_sub')}
            >
              {bars.length === 0 ? (
                <EmptyState text={t('today_hours_empty')} />
              ) : (
                <>
                  <p className="mobile-only muted">
                    {belowTarget > 0
                      ? t('today_below_target_line', {
                          n: belowTarget,
                          total: data.punched_out.length,
                        })
                      : t('today_below_target_none')}
                  </p>
                  <div className="desktop-only">
                    <BarChart bars={bars} format={hm} targetLabel={t('att_target')} />
                    <div className="legend-dots">
                      {isToday && (
                        <span>
                          <i style={{ background: 'var(--teal)' }} />
                          {t('today_in')}
                        </span>
                      )}
                      <span>
                        <i style={{ background: 'var(--violet)' }} />
                        {isToday ? t('today_out') : t('att_present')}
                      </span>
                      <span>
                        <i style={{ background: 'var(--amber)' }} />
                        {t('today_below_target')}
                      </span>
                      {/* The dashed lines in the chart, named with the values they stand for,
                          so the reader never has to hover a line to find out what it is. */}
                      <span>
                        <i className="legend-dash" />
                        {t('today_target_line')}
                        {targetLevels.length > 0 && ` · ${targetLevels.map(hm).join(' · ')}`}
                      </span>
                    </div>
                  </div>
                </>
              )}
            </Card>
            <Card
              className="span-4"
              title={t('today_week_chart')}
              subtitle={
                week
                  ? t('today_week_chart_sub_dates', {
                      from: fmtDate(week.week_start, false),
                      to: fmtDate(week.days[week.days.length - 1]?.date ?? week.week_start, false),
                    })
                  : t('today_week_chart_sub')
              }
            >
              {!week || week.days.length === 0 ? (
                <EmptyState text={t('today_week_empty')} />
              ) : (
                <>
                  <StackedMini
                    valueOf={(parts) => parts[0]?.value ?? 0}
                    days={week.days
                      .filter((d) => !d.is_future)
                      .map((d) => ({
                        label: `${fmtWeekday(d.date)} ${d.date.slice(8)}`,
                        parts: [
                          { value: d.present, tone: 'green', label: t('hours_st_present') },
                          { value: d.absent, tone: 'red', label: t('hours_st_absent') },
                          { value: d.off, tone: 'neutral', label: t('att_off') },
                          { value: d.holiday, tone: 'blue', label: t('hours_st_holiday') },
                        ],
                      }))}
                  />
                  <div className="legend-dots">
                    <span>
                      <i style={{ background: 'var(--green)' }} />
                      {t('hours_st_present')}
                    </span>
                    <span>
                      <i style={{ background: 'var(--red)' }} />
                      {t('hours_st_absent')}
                    </span>
                    <span>
                      <i style={{ background: 'var(--surface-3)' }} />
                      {t('att_off')}
                    </span>
                    <span>
                      <i style={{ background: 'var(--blue)' }} />
                      {t('hours_st_holiday')}
                    </span>
                  </div>
                </>
              )}
            </Card>
          </div>

          <p className="muted">
            {isToday
              ? t('today_refresh', { time: time(data.server_time) })
              : t('att_from_summaries')}
          </p>
        </>
      )}
    </div>
  );
}
